import { api } from './api';
import type { Sessie, SessieAanmakenAanvraag, SessieStatus, AxisStatusDto, AxisStatus, ActieveAssenResponse, SessieBesprekingDto, SessieBeantwoordenStatusDto } from '@types';

// ============================================
// Sessie API Functions
// ============================================

/**
 * Haal alle sessies op voor de huidige organisatie
 * @returns Array van sessies met sjabloon informatie
 */
export const getSessies = async (): Promise<Sessie[]> => {
  const response = await api.get<Sessie[]>('/sessies');
  return response.data;
};

/**
 * Haal een specifieke sessie op door sessieCode
 * @param sessieCode - De sessie code
 * @returns De sessie met details
 */
export const getSessieBySessieCode = async (sessieCode: string): Promise<Sessie> => {
  const response = await api.get<Sessie>(`/sessies/${sessieCode}`);
  return response.data;
};


/**
 * Maak een nieuwe sessie aan
 * @param aanvraag - SessieAanmakenAanvraag met naam en optioneel sjabloonId
 * @returns De aangemaakte sessie
 */
export const createSessie = async (aanvraag: SessieAanmakenAanvraag): Promise<Sessie> => {
  const response = await api.post<Sessie>('/sessies', aanvraag);
  return response.data;
};

/**
 * Werk de sessie status bij
 * @param sessieCode - De sessie code
 * @param status - De nieuwe status
 * @returns De geüpdatete sessie
 */
export const updateSessieStatus = async (sessieCode: string, status: SessieStatus): Promise<Sessie> => {
  const response = await api.patch<Sessie>(`/sessies/${sessieCode}/status`, { status });
  return response.data;
};

/**
 * Haal sessies op voor een specifieke begeleider
 * @param begeleiderId - Het ID van de begeleider
 * @param status - Optionele status filter (bijv. 'ACTIEF')
 * @returns Array van sessies voor de begeleider
 */
export const getSessiesByBegeleider = async (begeleiderId: number, status?: string): Promise<Sessie[]> => {
  const url = status ? `/sessies/begeleider/${begeleiderId}?status=${status}` : `/sessies/begeleider/${begeleiderId}`;
  const response = await api.get<Sessie[]>(url);
  return response.data;
};

// ============================================
// Axis Status Functions (gerelateerd aan sessies)
// ============================================

/**
 * Haal axis statusen op voor een sessie via sessiecode
 * @param sessieCode - De sessie code
 * @returns Lijst met axis statusen
 */
export const getAxisStatusenBySessieCode = async (sessieCode: string): Promise<AxisStatusDto[]> => {
  const response = await api.get<AxisStatusDto[]>(`/assen/sessie/${sessieCode}`);
  return response.data;
};

/**
 * Werk de status van een axis in een sessie bij via sessiecode
 * @param axisId - Het ID van de axis
 * @param sessieCode - De sessie code
 * @param status - De nieuwe status
 * @returns De geüpdatete sessie
 */
export const updateAxisInSessieStatusByCode = async (axisId: number, sessieCode: string, status: AxisStatus): Promise<Sessie> => {
  const response = await api.patch<Sessie>(`/sessies/${sessieCode}/assen/${axisId}/status`, { status });
  return response.data;
};

/**
 * Haal de actieve assen versie op voor een sessie (voor polling)
 * @param sessieCode - De sessie code
 * @returns De versie als integer
 */
export const getOpenAssenVersieBySessieCode = async (sessieCode: string, deelnemerToken: string): Promise<number> => {
  const response = await api.get<number>(`/assen/open-versie/${sessieCode}/token/${deelnemerToken}`);
  return response.data;
};

/**
 * Haal de actieve assen op voor een sessie
 * @param sessieCode - De sessie code
 * @returns ActieveAssenResponse met assen en stellingen
 */
export const getOpenAssenBySessieCode = async (sessieCode: string): Promise<ActieveAssenResponse> => {
  const response = await api.get<ActieveAssenResponse>(`/assen/open/${sessieCode}`);
  return response.data;
};

// ============================================
// Rapportage/Bespreken Functions (gerelateerd aan sessies)
// ============================================

/**
 * Haal beantwoorden status data op (voor beantwoorden dashboard)
 * @param sessieCode - De sessie code
 * @returns SessieBeantwoordenStatusDto met axis statusen, deelnemers progress, en percentages
 */
export const getBeantwoordenStatusBySessieCode = async (sessieCode: string): Promise<SessieBeantwoordenStatusDto> => {
  const response = await api.get<SessieBeantwoordenStatusDto>(`/sessies/${sessieCode}/beantwoorden-status`);
  return response.data;
};

/**
 * Haal sessie bespreking data op (voor bespreken dashboard)
 * @param sessieCode - De sessie code
 * @returns SessieBesprekingDto met complete boomstructuur en statistieken
 */
export const getSessieBesprekingData = async (sessieCode: string): Promise<SessieBesprekingDto> => {
  const response = await api.get<SessieBesprekingDto>(`/antwoorden/sessie/${sessieCode}`);
  return response.data;
};
